/**
 * Represents the tag type
 * ```typescript
 * // Load an existing PDF document
 * let document: PdfDocument = new PdfDocument(data, password);
 * // Initialize a new instance of the `PdfDataExtractor` class
 * let extractor: PdfDataExtractor = new PdfDataExtractor(document);
 * // Get the page from the PDF document
 * let page: PdfPage = document.getPage(0);
 * // Extract structure elements from a particular page in the PDF document
 * let pageElements: PdfStructureElement[] = extractor.getStructureElements(page);
 * // Extract the tag type of the first element
 * let element: PdfStructureElement = pageElements[0];
 * let tag: PdfTagType = element.tagType;
 * // Save the document
 * document.save('output.pdf');
 * // Destroy the document
 * document.destroy();
 * ```
 */
export var PdfTagType;
(function (PdfTagType) {
    /**
     * Specifies the type of `annotation` tag.
     */
    PdfTagType[PdfTagType["annotation"] = 0] = "annotation";
    /**
     * Specifies the type of `article` tag.
     */
    PdfTagType[PdfTagType["article"] = 1] = "article";
    /**
     * Specifies the type of `bibliographyEntry` tag.
     */
    PdfTagType[PdfTagType["bibliographyEntry"] = 2] = "bibliographyEntry";
    /**
     * Specifies the type of `blockQuotation` tag.
     */
    PdfTagType[PdfTagType["blockQuotation"] = 3] = "blockQuotation";
    /**
     * Specifies the type of `caption` tag.
     */
    PdfTagType[PdfTagType["caption"] = 4] = "caption";
    /**
     * Specifies the type of `code` tag.
     */
    PdfTagType[PdfTagType["code"] = 5] = "code";
    /**
     * Specifies the type of `division` tag.
     */
    PdfTagType[PdfTagType["division"] = 6] = "division";
    /**
     * Specifies the type of `document` tag.
     */
    PdfTagType[PdfTagType["documentType"] = 7] = "documentType";
    /**
     * Specifies the type of `figure` tag.
     */
    PdfTagType[PdfTagType["figure"] = 8] = "figure";
    /**
     * Specifies the type of `form` tag.
     */
    PdfTagType[PdfTagType["form"] = 9] = "form";
    /**
     * Specifies the type of `formula` tag.
     */
    PdfTagType[PdfTagType["formula"] = 10] = "formula";
    /**
     * Specifies the type of `heading` tag.
     */
    PdfTagType[PdfTagType["heading"] = 11] = "heading";
    /**
     * Specifies the type of `headingLevel1` tag.
     */
    PdfTagType[PdfTagType["headingLevel1"] = 12] = "headingLevel1";
    /**
     * Specifies the type of `headingLevel2` tag.
     */
    PdfTagType[PdfTagType["headingLevel2"] = 13] = "headingLevel2";
    /**
     * Specifies the type of `headingLevel3` tag.
     */
    PdfTagType[PdfTagType["headingLevel3"] = 14] = "headingLevel3";
    /**
     * Specifies the type of `headingLevel4` tag.
     */
    PdfTagType[PdfTagType["headingLevel4"] = 15] = "headingLevel4";
    /**
     * Specifies the type of `headingLevel5` tag.
     */
    PdfTagType[PdfTagType["headingLevel5"] = 16] = "headingLevel5";
    /**
     * Specifies the type of `headingLevel6` tag.
     */
    PdfTagType[PdfTagType["headingLevel6"] = 17] = "headingLevel6";
    /**
     * Specifies the type of `index` tag.
     */
    PdfTagType[PdfTagType["index"] = 18] = "index";
    /**
     * Specifies the type of `label` tag.
     */
    PdfTagType[PdfTagType["label"] = 19] = "label";
    /**
     * Specifies the type of `link` tag.
     */
    PdfTagType[PdfTagType["link"] = 20] = "link";
    /**
     * Specifies the type of `list` tag.
     */
    PdfTagType[PdfTagType["list"] = 21] = "list";
    /**
     * Specifies the type of `listBody` tag.
     */
    PdfTagType[PdfTagType["listBody"] = 22] = "listBody";
    /**
     * Specifies the type of `listItem` tag.
     */
    PdfTagType[PdfTagType["listItem"] = 23] = "listItem";
    /**
     * Specifies the type of `note` tag.
     */
    PdfTagType[PdfTagType["note"] = 24] = "note";
    /**
     * Specifies the type of `paragraph` tag.
     */
    PdfTagType[PdfTagType["paragraph"] = 25] = "paragraph";
    /**
     * Specifies the type of `part` tag.
     */
    PdfTagType[PdfTagType["part"] = 26] = "part";
    /**
     * Specifies the type of `private` tag.
     */
    PdfTagType[PdfTagType["privateType"] = 27] = "privateType";
    /**
     * Specifies the type of `quotation` tag.
     */
    PdfTagType[PdfTagType["quotation"] = 28] = "quotation";
    /**
     * Specifies the type of `reference` tag.
     */
    PdfTagType[PdfTagType["reference"] = 29] = "reference";
    /**
     * Specifies the type of `ruby` tag.
     */
    PdfTagType[PdfTagType["ruby"] = 30] = "ruby";
    /**
     * Specifies the type of `section` tag.
     */
    PdfTagType[PdfTagType["section"] = 31] = "section";
    /**
     * Specifies the type of `span` tag.
     */
    PdfTagType[PdfTagType["span"] = 32] = "span";
    /**
     * Specifies the type of `table` tag.
     */
    PdfTagType[PdfTagType["table"] = 33] = "table";
    /**
     * Specifies the type of `tableBodyRowGroup` tag.
     */
    PdfTagType[PdfTagType["tableBodyRowGroup"] = 34] = "tableBodyRowGroup";
    /**
     * Specifies the type of `tableDataCell` tag.
     */
    PdfTagType[PdfTagType["tableDataCell"] = 35] = "tableDataCell";
    /**
     * Specifies the type of `tableFooterRowGroup` tag.
     */
    PdfTagType[PdfTagType["tableFooterRowGroup"] = 36] = "tableFooterRowGroup";
    /**
     * Specifies the type of `tableHeader` tag.
     */
    PdfTagType[PdfTagType["tableHeader"] = 37] = "tableHeader";
    /**
     * Specifies the type of `tableHeaderRowGroup` tag.
     */
    PdfTagType[PdfTagType["tableHeaderRowGroup"] = 38] = "tableHeaderRowGroup";
    /**
     * Specifies the type of `tableOfContent` tag.
     */
    PdfTagType[PdfTagType["tableOfContent"] = 39] = "tableOfContent";
    /**
     * Specifies the type of `tableOfContentItem` tag.
     */
    PdfTagType[PdfTagType["tableOfContentItem"] = 40] = "tableOfContentItem";
    /**
     * Specifies the type of `tableRow` tag.
     */
    PdfTagType[PdfTagType["tableRow"] = 41] = "tableRow";
    /**
     * Specifies the type of `warichu` tag.
     */
    PdfTagType[PdfTagType["warichu"] = 42] = "warichu";
    /**
     * Specifies the type of `none` tag.
     */
    PdfTagType[PdfTagType["none"] = 43] = "none";
})(PdfTagType || (PdfTagType = {}));
/**
 * Represents the scope type of tag.
 * ```typescript
 * // Load an existing PDF document
 * let document: PdfDocument = new PdfDocument(data, password);
 * // Initialize a new instance of the `PdfDataExtractor` class
 * let extractor: PdfDataExtractor = new PdfDataExtractor(document);
 * // Get the page from the PDF document
 * let page: PdfPage = document.getPage(0);
 * // Extract structure elements from a particular page in the PDF document
 * let pageElements: PdfStructureElement[] = extractor.getStructureElements(page);
 * // Extract the scope type of the first element
 * let element: PdfStructureElement = pageElements[0];
 * let scope: ScopeType = element.scope;
 * // Save the document
 * document.save('output.pdf');
 * // Destroy the document
 * document.destroy();
 * ```
 */
export var ScopeType;
(function (ScopeType) {
    /**
     * Specifies `row` type of scope.
     */
    ScopeType[ScopeType["row"] = 0] = "row";
    /**
     * Specifies `column` type of scope.
     */
    ScopeType[ScopeType["column"] = 1] = "column";
    /**
     * Specifies `both` scope, meaning row and column types of scope are specified.
     */
    ScopeType[ScopeType["both"] = 2] = "both";
    /**
     * Specifies `none` scope, meaning no scope type is specified.
     */
    ScopeType[ScopeType["none"] = 3] = "none";
})(ScopeType || (ScopeType = {}));
